/**
 * User → Employee link client.
 *
 * Backs the EmployeeLinkModal that pops when a sign-off action returns
 * 422 EMPLOYEE_RECORD_REQUIRED. The modal calls fetchMyEmployeeLink to
 * get candidates, then linkMyUserToEmployee with the user's choice.
 *
 * Server-side, the matching service auto-links silently if there's
 * exactly one full-name match — most users will never see the modal.
 * It only appears when the platform can't decide (multiple matches
 * or zero matches).
 */

import { apiFetch } from './api';

export interface PublicLinkedEmployee {
  id: string;
  firstName: string;
  lastName: string;
  jobTitle: string | null;
  employmentStatus: string;
  linkMethod: 'already' | 'auto' | 'explicit_existing' | 'created_from_user';
}

export interface PublicEmployeeCandidate {
  id: string;
  firstName: string;
  lastName: string;
  jobTitle: string | null;
  employmentStatus: string;
}

export interface MyEmployeeLinkResult {
  linked: PublicLinkedEmployee | null;
  candidates: PublicEmployeeCandidate[];
}

export type LinkEmployeePayload =
  | { mode: 'existing'; employeeId: string }
  | { mode: 'create' };

export async function fetchMyEmployeeLink(
  signal?: AbortSignal,
): Promise<MyEmployeeLinkResult> {
  return apiFetch<MyEmployeeLinkResult>('/api/auth/me/employee', {
    method: 'GET',
    signal,
  });
}

export async function linkMyUserToEmployee(
  payload: LinkEmployeePayload,
): Promise<PublicLinkedEmployee> {
  return apiFetch<PublicLinkedEmployee>('/api/auth/me/link-employee', {
    method: 'POST',
    body: payload,
  });
}

/* ------------------------------------------------------------------ */
/* 2026-06-10 — Users & Permissions "My account" block.                */
/* ------------------------------------------------------------------ */

export interface MePublicUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  /** Two-factor sign-in is on for this account. Optional: older responses lack it. */
  mfaEnabled?: boolean;
}

/** The signed-in tenant user (GET /api/auth/me — user + their ALLOWED
 *  sidebar sections; allow-list, default OFF, directors exempt by role).
 *
 *  `dashboardTabs` (2026-09-08) is the same idea one level down: which of the
 *  Dashboard's tabs this person may open. Separate from `allowedSections`
 *  because those are sidebar SECTIONS and these are tabs inside one. A director
 *  gets every tab whatever it holds, so check the role first. */
export interface MeResponse {
  user: MePublicUser;
  /**
   * 2026-09-30 — a direct line the person sets themselves, for their business
   * card and email signature. Null for the many who never set one.
   *
   * Top-level rather than on `user` because `MePublicUser` mirrors what login
   * and refresh also return, and those do not carry this column — one type
   * arriving populated from one endpoint and null from another is the kind of
   * difference nobody notices until it is printed on a card.
   */
  directPhone: string | null;
  allowedSections: string[];
  /**
   * 2026-10-30 — of the sections above, the ones this person may only LOOK
   * at. Screens grey out what they cannot press; the server refuses a writing
   * method whatever the screen shows, so this is an affordance, not the gate.
   */
  readonlySections: string[];
  /**
   * 2026-10-14 — which SUB-TABS are turned off for this person.
   *
   * A DENY list: empty means every sub-tab of the sections above, which is
   * what everybody has and what a tab added to the platform next month is
   * until somebody turns it off. The sidebar drops the children named here,
   * and the router refuses one typed into the address bar.
   */
  blockedSubtabs: string[];
  dashboardTabs: string[];
}

/**
 * Set or clear the signed-in person's own direct line.
 *
 * The one field on My Profile they may change themselves: it is theirs, it
 * goes on a card with their name on it, and nobody else knows the day it
 * changes. Name, email and role stay with a director. `null` clears it.
 */
export async function setMyDirectPhone(
  directPhone: string | null,
): Promise<{ directPhone: string | null }> {
  return apiFetch<{ directPhone: string | null }>('/api/auth/me/direct-phone', {
    method: 'PATCH',
    body: { directPhone },
  });
}

export async function fetchMe(signal?: AbortSignal): Promise<MeResponse> {
  return apiFetch<MeResponse>('/api/auth/me', { method: 'GET', signal });
}

/** Self-service password change for the signed-in user. The current
 *  password is required as proof; the admin-on-behalf path is
 *  resetTenantUserPassword in tenantUsersApi. */
export async function changeMyPassword(input: {
  currentPassword: string;
  newPassword: string;
}): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>('/api/auth/change-password', {
    method: 'POST',
    body: input,
  });
}

/**
 * End every session this login holds, this one included.
 *
 * The caller signs out afterwards: the refresh token is dead, so staying on
 * the page only means waiting for the access token to expire mid-click.
 */
export async function signOutEverywhere(): Promise<{ revoked: number }> {
  return apiFetch<{ revoked: number }>('/api/auth/sign-out-everywhere', {
    method: 'POST',
    body: {},
  });
}

/** 2026-06-10 — interaction-aware presence. AppShell sends this ~once a
 *  minute ONLY while the user is genuinely interacting (real input events
 *  + visible tab); it stamps last_seen_at for the "Signed in: Online" pill
 *  in Users & Permissions. */
export async function sendHeartbeat(): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>('/api/auth/heartbeat', { method: 'POST' });
}
